import React from 'react';
import PropTypes from 'prop-types';
import { find, findIndex, get, head, memoize, some, take, includes, isEmpty, keys, forEach } from 'lodash';

import Config from 'config';
import mediator, { ACTIONS } from 'mediator';
import { ERROR_CONSTANTS } from 'constants';

import API from 'lib/tessitura/API';
import metrics from 'lib/common/metrics';
import features from 'lib/common/features';
import panToElement from 'lib/seatmaps/panToElement';
import processMiniCart from 'lib/cart/processMiniCart';
import excludeLockedItems from 'lib/cart/excludeLockedItems';
import repopulateSubLineItem from 'lib/cart/repopulateSubLineItem';
import pricesForZone from 'lib/pricing/pricesForZone';
import findSeatView from 'lib/seats/findSeatView';
import { findSeatEl } from 'lib/seats/seatFinders';
import logger from 'lib/common/logger';
import { cartContainsRestrictedTickets } from 'lib/cart/ticketRestrictions';

import Modal from 'components/Modal';
import Basket from 'components/Basket';
import SeatDetails from 'components/SeatDetails';
import AdaAttestation from 'components/AdaAttestation';

function getCurrentPerformance(performances, id) {
  return find(performances, perf => parseInt(perf.perf_no, 10) === id);
}

let config;

class DetailsContainer extends React.Component {
  constructor(props) {
    super(props);
    config = new Config().getConfig();

    this.state = {
      /**
       * The users selected seats
       *
       * TODO: rename to selectedSeats to avoid
       * confusion with Tessitura line items.
       */
      lineItems: [],

      /**
       * The currently selected seat
       */
      selectedSeat: null,

      /**
       * The users preferred price type
       * Set based on their initial selection
       */
      preferredPriceType: null,

      /**
       * Available upsell zones
       */
      upsells: [],

      /**
       * Any notices to be shown to the user
       * e.g. max tickets limit, processing error etc.
       */
      noticeMessage: null,

      /**
       * Is the cart currently processing?
       * Used to lock off UI whilst booking seats.
       */
      isProcessing: false,

      /**
       * Have the tickets been successfully
       * added to the cart?
       */
      isSuccessful: false,

      showAdaAttestation: false,

      adaAttestationSpecialRequests: null
    };

    this.getSeatViewForSelection = this.getSeatViewForSelection.bind(this);
    this.getPriceTypesForSelection = this.getPriceTypesForSelection.bind(this);
    this.getAdditionalPriceTypesForSelection = this.getAdditionalPriceTypesForSelection.bind(this);
    this.findUpsell = this.findUpsell.bind(this);
    this.onSelectUpsell = this.onSelectUpsell.bind(this);
    this.handleSeatSelection = this.handleSeatSelection.bind(this);
    this.handlePriceTypeSelection = this.handlePriceTypeSelection.bind(this);
    this.handleDeselection = this.handleDeselection.bind(this);
    this.handleReselection = this.handleReselection.bind(this);
    this.handleRemoval = this.handleRemoval.bind(this);
    this.handlePriceTypeChange = this.handlePriceTypeChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);

    this.handleRefreshSeatmap = this.handleRefreshSeatmap.bind(this);
    this.renderEnhancedNotice = this.renderEnhancedNotice.bind(this);
    this.renderSeatDetails = this.renderSeatDetails.bind(this);
    this.renderBasket = this.renderBasket.bind(this);
    this.validateQuantity = this.validateQuantity.bind(this);
    this.seatQuantityError = this.seatQuantityError.bind(this);

    this.handleAcceptAdaAttestation = this.handleAcceptAdaAttestation.bind(
      this
    );
    this.handleDeclineAdaAttestation = this.handleDeclineAdaAttestation.bind(
      this
    );
  }

  componentWillMount() {
    mediator.subscribe(ACTIONS.SEAT_SELECTED, this.handleSeatSelection);
  }

  componentDidMount() {
    let currentCartItems = this.props.currentCartItems.filter(item => {
      if (this.props.mode === 'PERFORMANCE') {
        return item.perf_no === this.props.currentPerformanceId;
      }

      return item.pkg_no === this.props.currentPackageNo || item.perf_no === this.props.currentPerformanceId;
    });

    let lockedPriceTypes;
    if (this.props.isFixedPackagePerformance) {
      lockedPriceTypes = features.getOption('lockedSeats', 'lockedPriceTypes');
    }

    /**
     * Show existing cart items in the mini-cart and on the map.
     */
    if (currentCartItems.length) {
      /**
       * Map cart items into a full object
       * w/ seat data and seat element etc.
       */
      let existingItems = currentCartItems.map(item => {
        return repopulateSubLineItem(
          item,
          this.props.seatData,
          [].concat(this.props.prices, this.props.additionalPrices),
          this.props.performances,
          lockedPriceTypes
        );
      });

      /**
       * Mark cart seats as selected in seat-map
       */
      existingItems.forEach(item => {
        if (item && item.customSeatEl) {
          mediator.publish(ACTIONS.SEAT_TOGGLE_SELECTED, {
            seatEl: item.customSeatEl
          });
        } else if (item && item.seatEl) {
          mediator.publish(ACTIONS.SEAT_TOGGLE_SELECTED, {
            seatEl: item.seatEl
          });
        }
      });

      /**
       * Merge existing cart-items with newly selected items
       */
      this.setState(prevState => {
        return {
          lineItems: existingItems
            .concat(prevState.lineItems)
            .filter(x => x !== undefined)
        };
      });
    }

    /**
     * Fetch upsells
     */
    if (features.isEnabled('inlineSeatUpsell')) {
      API.SeatUpsells().then(remoteData => {
        if (remoteData) {
          this.setState({
            upsells: remoteData
          });
        }
      });
    }
  }

  componentWillUnmount() {
    mediator.unsubscribe(ACTIONS.SEAT_SELECTED, this.handleSeatSelection);
  }

  getSeatViewForSelection(selection) {
    /* eslint-disable no-empty */
    let seatView;
    try {
      seatView = findSeatView(
        this.props.seatData,
        this.props.seatViews,
        selection
      );
    } catch (e) {}
    return seatView;
  }

  getPriceTypesForSelection(seat) {
    return pricesForZone(this.props.prices, seat.seatData.zone_no);
  }

  getAdditionalPriceTypesForSelection(seat) {
    return pricesForZone(this.props.additionalPrices, seat.seatData.zone_no);
  }

  findUpsell() {
    if (!features.isEnabled('inlineSeatUpsell')) {
      return;
    }

    const { upsells, selectedSeat } = this.state;

    /**
     * Find the first upsell for the current price-zone
     */
    const upsellDetails = find(upsells, function(upsell) {
      const selectionZone = parseInt(selectedSeat.seatData.zone_no, 10);
      const upsellZone = parseInt(upsell.original_zone_no, 10);
      return selectionZone === upsellZone;
    });

    const upsellMatch =
      upsellDetails &&
      find(this.props.seatData, function(seat) {
        return (
          seat.zone_no == upsellDetails.upgrade_zone_no &&
          seat.seat_status.id == '0'
        );
      });

    /**
     * Get a matching seat from the upsold price zone
     */
    if (upsellMatch) {
      const upsoldSeat = {
        seatEl: findSeatEl(upsellMatch),
        seatData: upsellMatch
      };

      return {
        seat: upsoldSeat,
        seatView: this.getSeatViewForSelection(upsoldSeat),
        prices: take(
          pricesForZone(this.props.prices, upsoldSeat.seatData.zone_no),
          1
        ),
        referencePriceType: head(this.getPriceTypesForSelection(selectedSeat)),
        performanceId: this.props.currentPerformanceId
      };
    } else {
      return;
    }
  }

  onSelectUpsell(upsoldSeat, priceType) {
    const panZoomInstance = this.props.mainPanZoomInstance;

    // Deselect original seat
    this.handleDeselection(this.state.selectedSeat, true);

    // Pan to new seat element
    panToElement(upsoldSeat.seatEl, panZoomInstance, panZoomInstance.getZoom());

    // Select up-sold seat
    mediator.publish(ACTIONS.SEAT_ADDED, upsoldSeat);
    this.handlePriceTypeSelection(upsoldSeat, priceType);
  }

  handleSeatSelection(selection) {
    const { lineItems, isProcessing } = this.state;

    if (isProcessing) {
      return;
    }

    /**
     * Is the selection already selected i.e. trying to de-select a seat
     */
    const isSelectedSeatAlreadyInMiniCart = some(lineItems, function(item) {
      return item.seatData.seat_no === selection.seatData.seat_no;
    });

    let showMaxTicketsMessage = false;

    // Special limit checking, based on config
    const { primaryPriceTypes, addOnPriceTypes } = this.props.priceTypeOptions;

    let primaryTicketsFulfilled = false;
    let addOnTicketsFulfilled = false;

    if (primaryPriceTypes) {
      primaryTicketsFulfilled = keys(primaryPriceTypes).every(priceType => {
        const priceTypeLineItems = lineItems.filter(lineItem => {
          return parseInt(priceType, 10) === parseInt(lineItem.priceData.price_type, 10);
        });

        return parseInt(primaryPriceTypes[priceType].maxSeats) === priceTypeLineItems.length;
      });
    }

    if (addOnPriceTypes) {
      addOnTicketsFulfilled = keys(addOnPriceTypes).every(priceType => {
        const priceTypeLineItems = lineItems.filter(lineItem => {
          return parseInt(priceType, 10) === parseInt(lineItem.priceData.price_type, 10);
        });

        return parseInt(addOnPriceTypes[priceType].maxSeats) === priceTypeLineItems.length;
      });
    }

    showMaxTicketsMessage = (
      primaryTicketsFulfilled &&
      addOnTicketsFulfilled &&
      // Don't show the message if we are selecting an existing seat
      isSelectedSeatAlreadyInMiniCart === false
    );

    if (showMaxTicketsMessage) {
      this.setState({
        noticeMessage: config.maxTicketsMessage
      });
    } else {
      /**
       * Mark the seat as selected on the seat-map.
       */
      mediator.publish(ACTIONS.SEAT_ADDED, selection);

      /**
       * If we have the following:
       * - A preferred price type (we've already selected a seat)
       * - Some items in our mini-cart
       * - We've selected a seat of this type before (zone / hold code / seat type)
       * - We've not already selected the seat (were trying to de-select a seat?)
       * Then try to pre-select preferred price-type otherwise show the seat popup.
       */
      let shouldShowSeatDetails = true;

      let states = {
        noticeMessage: null
      };

      if (shouldShowSeatDetails) {
        states.selectedSeat = selection;
      }

      this.setState(states);
    }
  }

  handlePriceTypeSelection(seat, priceData) {
    const seatSelection = {
      seatEl: seat.seatEl,
      seatData: seat.seatData,
      priceData: priceData,

      //@TODO: Is this still needed?
      performance: getCurrentPerformance(
        this.props.performances,
        this.props.currentPerformanceId
      )
    };

    this.setState(
      prevState => {
        let newState = {
          // Add to the front without mutating original list
          lineItems: [seatSelection].concat(prevState.lineItems)
        };

        if (!prevState.preferredPriceType) {
          newState.preferredPriceType = seatSelection.priceData.price_type;
        }

        return newState;
      },
      () => {
        metrics.action(
          'Seat Selected',
          `Selected seat ${seatSelection.seatData.seat_num}, row ${
            seatSelection.seatData.seat_row
          }, screen ${seatSelection.seatData.screen_no}`
        );

        this.handleDeselection(seat, false);
      }
    );
  }

  handleDeselection(seat, deselect) {
    this.setState({ selectedSeat: null });
    if (deselect) {
      mediator.publish(ACTIONS.SEAT_DESELECTED, seat);
    }
  }

  handleReselection(lineItem) {
    mediator.publish(ACTIONS.SEAT_SELECTED, lineItem);
  }

  handleRemoval(lineItem) {
    let { lineItems, isProcessing } = this.state;

    if (!isProcessing) {
      mediator.publish(ACTIONS.SEAT_DESELECTED, lineItem);
      let idx = findIndex(lineItems, lineItem);

      // Remove item without mutating original list
      let newItems = lineItems.slice(0, idx).concat(lineItems.slice(idx + 1));

      this.setState(
        {
          lineItems: newItems,
          noticeMessage: null
        },
        () => {
          metrics.action(
            'Seat Removed',
            `Removed seat ${lineItem.seatData.seat_num}, row ${
              lineItem.seatData.seat_row.seat_row
            }, screen ${lineItem.seatData.screen_no}`
          );
        }
      );
    }
  }

  handlePriceTypeChange(lineItem, priceData) {
    const { lineItems, isProcessing } = this.state;

    const currentPerformance = getCurrentPerformance(
      this.props.performances,
      this.props.currentPerformanceId
    );

    if (!isProcessing) {
      const replacementLineItemData = {
        seatEl: lineItem.seatEl,
        seatData: lineItem.seatData,
        priceData: priceData,
        performance: currentPerformance
      };

      // Get the index of the existing item
      const idx = findIndex(lineItems, lineItem);

      // Add in the new line item, we are going to mutate the list here.
      const newItems = lineItems.slice(0, idx).concat(lineItems.slice(idx + 1));

      // Insert the new item at the same position as the old item.
      newItems.splice(idx, 0, replacementLineItemData);

      this.setState({
        lineItems: newItems,
        noticeMessage: null
      });
    }
  }

  getAdaSeatSelections(processableSeatSelections) {
    return processableSeatSelections.filter(
      x =>
        includes(
          features.getOption('adaAttestation', 'seatTypes'),
          x.seatData.seat_type
        ) ||
        includes(
          features.getOption('adaAttestation', 'holdCodes'),
          x.seatData.hc_no
        )
    );
  }

  hasAlreadyAcceptedAdaAttestation() {
    const { currentPerformanceId } = this.props;
    let alreadyAccepted = false;

    try {
      const adaMeta = localStorage.getItem(
        `PerformanceADAMeta-${currentPerformanceId}`
      );
      if (currentPerformanceId && adaMeta) {
        alreadyAccepted = true;
      }
    } catch (e) {} // eslint-disable-line no-empty

    return alreadyAccepted;
  }

  optionallyStoreAdaData() {
    const { currentPerformanceId } = this.props;
    const { adaAttestationSpecialRequests } = this.state;

    const shouldStore =
      currentPerformanceId && !isEmpty(adaAttestationSpecialRequests);

    if (shouldStore) {
      API.StoreADAData(JSON.stringify(adaAttestationSpecialRequests)).catch(
        function() {
          logger.captureMessage(
            `Failed to store PerformanceADAMeta for ${currentPerformanceId}`
          );
        }
      );
    }
  }

  validateQuantity(priceTypes) {
    let error = null;

    forEach(priceTypes, (limits, priceType) => {
      let multiples = null;
      let minSeats = 0;

      if ('multiples' in limits) {
        multiples = parseInt(limits.multiples, 10);
      }

      if ('minSeats' in limits) {
        minSeats = parseInt(limits.minSeats, 10);
      }

      const priceTypeLineItems = this.state.lineItems.filter(lineItem => {
        return parseInt(priceType, 10) === parseInt(lineItem.priceData.price_type, 10);
      }).length;

      if (minSeats && priceTypeLineItems && priceTypeLineItems < minSeats) {
        error = {
          price: parseInt(priceType, 10),
          seats: minSeats,
          type: 'minimum',
        }
      } else if (multiples && priceTypeLineItems % multiples !== 0) {
        error = {
          price: parseInt(priceType, 10),
          seats: multiples,
          type: 'multiples',
        }
      }
    });

    return error;
  }

  seatQuantityError(priceTypes, error) {
    const priceType = priceTypes.find(item => {
      return parseInt(item.price_type, 10) === error.price;
    });

    if (error.type === 'multiples') {
      return `You must select multiples of ${error.seats} ${priceType.alias_description || priceType.description} tickets.`;
    }

    return `You must select at least ${error.seats} ${priceType.alias_description || priceType.description} tickets.`;
  }

  handleSubmit(e) {
    if (e !== undefined) {
      e.stopPropagation();
    }

    this.setState({
      isProcessing: true
    });

    /**
     * Exclude locked items from processing
     */
    let processableLineItems = this.state.lineItems.filter(
      item => !item.isLocked
    );

    let currentCartItems = excludeLockedItems(
      this.state.lineItems,
      this.props.currentCartItems
    );

    /**
     * Make sure minimum requirements are met
     */
    const primaryPriceTypes = get(this.props.priceTypeOptions, 'primaryPriceTypes', null);
    const addOnPriceTypes = get(this.props.priceTypeOptions, 'addOnPriceTypes', null);

    let primaryLimitNotMet = null;
    let addOnLimitNotMet = null;

    if (addOnPriceTypes) {
      primaryLimitNotMet = this.validateQuantity(primaryPriceTypes);
    }

    if (primaryPriceTypes) {
      addOnLimitNotMet = this.validateQuantity(addOnPriceTypes);
    }

    let limitError = false;

    if (primaryLimitNotMet) {
      limitError = this.seatQuantityError(this.props.priceTypes, primaryLimitNotMet);
    } else if (addOnLimitNotMet) {
      limitError = this.seatQuantityError(this.props.additionalPriceTypes, addOnLimitNotMet);
    }

    let restricted = cartContainsRestrictedTickets(this.state.lineItems);

    let requiresAdaAttestation =
      features.isEnabled('adaAttestation') &&
      this.getAdaSeatSelections(processableLineItems).length > 0 &&
      !this.hasAlreadyAcceptedAdaAttestation() &&
      !this.state.adaAttestationSpecialRequests;

    if (requiresAdaAttestation) {
      this.setState({
        showAdaAttestation: true
      });
    } else {
      if (!limitError && !restricted) {
        const params = {
          mode: this.props.mode,
          cartItems: currentCartItems,
          lineItems: processableLineItems,
          performanceNo: this.props.currentPerformanceId,
          packageNo: this.props.currentPackageNo,
          currentFixedPackage: this.props.currentFixedPackage,
          currentFlexPackage: this.props.currentFlexPackage,
          currentFlexPackagePerformance: this.props.currentFlexPackagePerformance,
          performanceGroupNo: this.props.performanceGroupNo,
          priceTypes: this.props.priceTypes.map(type => {
            return type.price_type;
          }),
          additionalPriceTypes: this.props.additionalPriceTypes.map(type => {
            return type.price_type;
          })
        };

        processMiniCart(params).then(response => {
          this.setState({
            isSuccessful: true
          });

          this.optionallyStoreAdaData();

          this.props.onComplete.call(null, response);
        }).catch(error => {
          this.setState({
            isProcessing: false
          });

          if (error && error.message) {
            this.setState({
              noticeMessage: error.message
            });
          }
        });
      } else {
        this.setState({
          noticeMessage: limitError || restricted,
          isProcessing: false
        });
      }
    }
  }

  handleAcceptAdaAttestation(adaChoices) {
    let adaAttestationSpecialRequests = {};

    if (adaChoices) {
      adaAttestationSpecialRequests = adaChoices;
    }

    this.setState(
      {
        showAdaAttestation: false,
        adaAttestationSpecialRequests: adaAttestationSpecialRequests
      },
      () => {
        this.handleSubmit();
      }
    );
  }

  handleDeclineAdaAttestation() {
    this.setState({
      isProcessing: false,
      showAdaAttestation: false
    });
  }

  /**
   * Handle refreshing the seat map.
   * Used if there was a pottential seat collision.
   * Gives the user the option to refresh the feed and try again.
   */
  handleRefreshSeatmap() {
    if (!this.props.currentPerformanceId) {
      return;
    }

    this.setState(
      {
        lineItems: []
      },
      () => {
        metrics.action(
          'Refresh seat availability',
          `Refresh seat availability for performance ${
            this.props.currentPerformanceId
          }`
        );

        this.props.onChangePerformance(this.props.currentPerformanceId);
      }
    );
  }

  renderEnhancedNotice() {
    if (this.state.noticeMessage === ERROR_CONSTANTS.SEATS_UNAVAILABLE) {
      return (
        <Modal
          onClose={() => {
            this.setState({
              noticeMessage: null
            });
          }}
        >
          <div className="syos-enhanced-notice">
            <div className="syos-enhanced-notice__inner">
              <h3 className="syos-enhanced-notice__title">
                One or more of your seats could not be reserved
              </h3>
              <div className="syos-enhanced-notice__message">
                <p>
                  This may be because someone has already reserved one of your
                  selected seats. Try{' '}
                  <strong>refreshing the seat availability</strong> or{' '}
                  <strong>selecting other seats</strong>.
                </p>
              </div>
              <div className="syos-enhanced-notice__actions">
                <button
                  className="syos-button"
                  onClick={this.handleRefreshSeatmap}
                >
                  Refresh seat availability
                </button>
              </div>
            </div>
          </div>
        </Modal>
      );
    } else {
      return null;
    }
  }

  renderSeatDetails(selection) {
    /**
     * Check if we have selected a current cart item
     * so we can change the UI as needed.
     */
    let currentLineItem = selection
      ? find(this.state.lineItems, function(item) {
          return selection.seatData.seat_no === item.seatData.seat_no;
        })
      : null;

    const canShowSeatDetails = selection && !get(currentLineItem, 'isLocked');

    return canShowSeatDetails ? (
      <SeatDetails
        mode={this.props.mode}
        selectedSeat={selection}
        seatView={this.getSeatViewForSelection(selection)}
        priceTypes={this.getPriceTypesForSelection(selection)}
        additionalPriceTypes={this.getAdditionalPriceTypesForSelection(selection)}
        currentLineItem={currentLineItem}
        handleSelection={this.handlePriceTypeSelection}
        handleDeselection={this.handleDeselection}
        handleRemoval={this.handleRemoval}
        upsell={this.findUpsell()}
        onSelectUpsell={this.onSelectUpsell}
        performanceDetails={this.props.performanceDetails}
        priceTypeOptions={this.props.priceTypeOptions}
        lineItems={this.state.lineItems}
        currentFacility={this.props.currentFacility}
      />
    ) : null;
  }

  renderBasket(lineItems) {
    let basketButtonLabel = memoize(() => {
      // TODO: Convert to RemoteData style constants.
      if (this.state.isSuccessful) {
        return config.basket.successLabel;
      } else if (this.state.isProcessing) {
        return config.basket.processingLabel;
      } else if (this.props.currentCartItems.length) {
        return config.basket.updateLabel;
      } else {
        return config.basket.submitLabel;
      }
    });

    let noticeMessage = this.state.noticeMessage;
    let promoCodeMessage = get(this.props.activePromoCode, 'text2');
    if (!noticeMessage && promoCodeMessage) {
      noticeMessage = promoCodeMessage;
    }

    return lineItems.length ? (
      <Basket
        mode={this.props.mode}
        lineItems={lineItems}
        prices={this.props.prices}
        additionalPrices={this.props.additionalPrices}
        priceTypes={this.props.priceTypes}
        additionalPriceTypes={this.props.additionalPriceTypes}
        priceTypeOptions={this.props.priceTypeOptions}
        buttonLabel={basketButtonLabel()}
        noticeMessage={noticeMessage}
        handleSubmit={this.handleSubmit}
        handleRemoval={this.handleRemoval}
        handleReselection={this.handleReselection}
        handlePriceTypeChange={this.handlePriceTypeChange}
        handleRefreshSeatmap={this.handleRefreshSeatmap}
        isProcessing={this.state.isProcessing}
      />
    ) : null;
  }

  renderAdaAttestation() {
    let canShowAdaAttestation =
      this.state.showAdaAttestation && features.isEnabled('adaAttestation');

    return canShowAdaAttestation ? (
      <AdaAttestation
        handleSelection={this.handleAcceptAdaAttestation}
        handleDeselection={this.handleDeclineAdaAttestation}
        specialRequestOptions={features.getOption(
          'adaAttestation',
          'specialRequestOptions'
        )}
      />
    ) : null;
  }

  render() {
    return (
      <div className="syos-details-container">
        {this.renderEnhancedNotice()}
        {this.renderSeatDetails(this.state.selectedSeat)}
        {this.renderBasket(this.state.lineItems)}
        {this.renderAdaAttestation()}
      </div>
    );
  }
}

DetailsContainer.propTypes = {
  onComplete: PropTypes.func.isRequired,
  prices: PropTypes.array,
  additionalPrices: PropTypes.array,
  seatData: PropTypes.array,
  seatViews: PropTypes.array,
  currentCart: PropTypes.object,
  currentCartItems: PropTypes.array,
  currentFixedPackage: PropTypes.object,
  currentFlexPackage: PropTypes.object,
  currentFlexPackagePerformance: PropTypes.object,
  isFixedPackagePerformance: PropTypes.bool,
  performances: PropTypes.array,
  currentPerformanceId: PropTypes.number,
  currentPackageNo: PropTypes.number,
  performanceGroupNo: PropTypes.number,
  mainPanZoomInstance: PropTypes.object,
  activePromoCode: PropTypes.string,
  onChangePerformance: PropTypes.func.isRequired,
  performanceDetails: PropTypes.object,
  mode: PropTypes.string,
  priceTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  additionalPriceTypes: PropTypes.arrayOf(PropTypes.object),
  priceTypeOptions: PropTypes.object,
};

export default DetailsContainer;



// WEBPACK FOOTER //
// ./src/javascripts/containers/DetailsContainer.js